import React, { cloneElement, type ReactNode, useRef } from 'react'
import { toast, type ToastContentProps, Zoom } from 'react-toastify'
import { Icon } from '@awell-health/design-system'
import { t } from 'i18next'
import type { Notify, NotifyErrorOptions, NotifyOptions, ErrorWithMessage, NotifyError } from './types'

import { NotificationTypes } from './types'

interface Notifications {
  notify: Notify
  notifySuccess: Notify
  notifyInformative: Notify
  notifyError: NotifyError
  notifyDelete: Notify
}

const Notification = (props: ToastContentProps<NotifyOptions>): ReactNode => {
  const { icon = null, message, content = null, notifyType, actions = null } = props.data

  const { closeToast } = props

  let iconComponent: null | ReactNode = null
  switch (notifyType) {
    case NotificationTypes.error:
      iconComponent = <Icon icon="RiErrorWarningLine" className="fill-red-600" />
      break
    case NotificationTypes.success:
      iconComponent = <Icon icon="RiCheckboxLine" className="fill-green-600" />
      break
    case NotificationTypes.warning:
      iconComponent = <Icon icon="RiAlertLine" className="fill-yellow-500" />
      break
    case NotificationTypes.delete:
      iconComponent = <Icon icon="RiDeleteBinFill" className="fill-red-600" />
      break
    default:
      iconComponent = <Icon icon="RiInformationLine" className="fill-blue-300" />
      break
  }

  return (
    <div className="flex gap-2 items-start">
      <div className="">{icon ?? iconComponent}</div>
      <div className="flex flex-col gap-2">
        <div className="text-slate-800 text-[14px] font-semibold">{message}</div>
        {content !== null && <div className="text-slate-500 text-sm overflow-auto">{content}</div>}
        {actions !== null && <div className="flex gap-2 py-2">{cloneElement(actions, { closeToast })}</div>}
      </div>
    </div>
  )
}

function isErrorWithMessage(error: any): error is ErrorWithMessage {
  return typeof error === 'object' && error !== null && 'message' in error
}

/**
 * Hook for displaying toast notifications with built-in duplicate prevention.
 *
 * Features:
 * - Multiple notification types (error, success, informative, warning, delete)
 * - Automatic duplicate prevention (2-second window)
 * - Customizable content, icons, and behavior
 * - Integration with react-toastify
 *
 * Duplicate Prevention:
 * - Prevents identical notifications from showing within 2 seconds
 * - Uses notification type + message + error message as unique key
 * - Automatically cleans up old tracking data to prevent memory leaks
 *
 * @returns Object containing notification functions:
 * - notifyError: For error notifications (see function docs for usage guidelines)
 * - notify: Generic notification function
 * - notifySuccess: For success notifications
 * - notifyInformative: For informational notifications
 * - notifyDelete: For delete confirmation notifications
 */
export const useNotifications = (): Notifications => {
  const DEFAULT_TIME_IN_MS_BEFORE_HIDING = 3000
  const DUPLICATE_PREVENTION_WINDOW = 2000 // 2 seconds

  // Track recent notifications to prevent duplicates within 2-second window
  // This addresses TSUP-195 where error notifications were showing multiple times
  const recentNotifications = useRef<Map<string, number>>(new Map())

  const createNotificationKey = (message: string, notifyType: NotificationTypes, errorMessage?: string): string => {
    return `${notifyType}:${message}${errorMessage ? `:${errorMessage}` : ''}`
  }

  const isDuplicate = (key: string): boolean => {
    const now = Date.now()
    const lastShown = recentNotifications.current.get(key)

    if (lastShown && now - lastShown < DUPLICATE_PREVENTION_WINDOW) {
      return true
    }

    // Clean up old entries to prevent memory leaks
    for (const [k, timestamp] of recentNotifications.current.entries()) {
      if (now - timestamp > DUPLICATE_PREVENTION_WINDOW) {
        recentNotifications.current.delete(k)
      }
    }

    recentNotifications.current.set(key, now)
    return false
  }

  const notify = (props: NotifyOptions): void => {
    const {
      notifyType = NotificationTypes.informative,
      message,
      icon,
      content,
      actions,
      persist = false,
      closeOnClick = true
    } = props

    // Create a key for duplicate detection
    const notificationKey = createNotificationKey(message, notifyType)

    // Skip if this is a duplicate
    if (isDuplicate(notificationKey)) {
      return
    }

    let progressClassName = 'from-blue-200 to-blue-500'

    switch (notifyType) {
      case NotificationTypes.delete:
      case NotificationTypes.error:
        progressClassName = 'from-red-200 to-red-500'
        break
      case NotificationTypes.success:
        progressClassName = 'from-green-200 to-green-500'
        break
      case NotificationTypes.warning:
        progressClassName = 'from-yellow-200 to-yellow-500'
        break
    }

    toast(Notification, {
      data: {
        message,
        icon,
        content,
        actions,
        notifyType
      },
      hideProgressBar: false,
      position: 'bottom-center',
      closeOnClick,
      autoClose: persist ? false : DEFAULT_TIME_IN_MS_BEFORE_HIDING,
      closeButton: true,
      className: 'bg-white rounded-lg shadow border border-slate-200 w-[400px]',
      transition: Zoom,
      progressClassName: `bg-gradient-to-r h-[3px] ${progressClassName}`,
      icon: false
    })
  }

  /**
   * Displays an error notification with built-in duplicate prevention.
   *
   * ⚠️ IMPORTANT USAGE GUIDELINES:
   * - In custom hooks, ALWAYS wrap this in useEffect to prevent duplicate notifications
   * - Never call directly in component render/hook body when error state exists
   * - The function includes 2-second duplicate prevention for identical errors
   *
   * @example
   * ✅ CORRECT - Use with useEffect in custom hooks:
   * ```typescript
   * export const useMyData = () => {
   *   const { notifyError } = useNotifications()
   *   const { data, error } = useQuery(...)
   *
   *   useEffect(() => {
   *     if (error) {
   *       notifyError({ message: 'Failed to load data', error })
   *     }
   *   }, [error, notifyError])
   *
   *   return { data }
   * }
   * ```
   *
   * ❌ INCORRECT - Direct call in hook body:
   * ```typescript
   * export const useMyData = () => {
   *   const { notifyError } = useNotifications()
   *   const { data, error } = useQuery(...)
   *
   *   if (error) {
   *     notifyError({ message: 'Failed to load data', error }) // Called on every render!
   *   }
   *
   *   return { data }
   * }
   * ```
   *
   * @param options - Notification configuration
   * @param options.error - Error object (must have a 'message' property)
   * @param options.message - Custom message to display (optional)
   * @param options.content - Custom JSX content (optional, overrides default error display)
   * @param options.persist - Whether notification should stay until manually closed
   * @param options.closeOnClick - Whether clicking notification should close it
   */
  const notifyError = ({ error, content, ...rest }: NotifyErrorOptions): void => {
    if (isErrorWithMessage(error)) {
      // Create a more specific key for error notifications including the error message
      const notificationKey = createNotificationKey(rest.message || '', NotificationTypes.error, error.message)

      // Skip if this is a duplicate
      if (isDuplicate(notificationKey)) {
        return
      }

      notify({
        ...rest,
        notifyType: NotificationTypes.error,
        content: content ?? <div>{t('error_description', { message: error.message })}</div>
      })
    }
  }

  const notifySuccess = (props: NotifyOptions): void => {
    notify({
      ...props,
      notifyType: NotificationTypes.success
    })
  }

  const notifyInformative = (props: NotifyOptions): void => {
    notify({
      ...props,
      notifyType: NotificationTypes.informative
    })
  }

  const notifyDelete = (props: NotifyOptions): void => {
    notify({
      ...props,
      notifyType: NotificationTypes.delete
    })
  }

  return { notifyError, notify, notifySuccess, notifyInformative, notifyDelete }
}
